import { Check, Copy, EnvelopeSimple, LinkSimple, ShareNetwork } from './icons';
import { useState, type ReactNode } from 'react';
import { cn } from './cn';
import { Dialog } from './Dialog';
import { shareUrl, withStartTime, type ShareTarget } from './share';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
  /** Posição atual do vídeo: oferece "começar deste ponto". */
  currentTime?: number;
}

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export function ShareDialog({ open, onClose, title, url, currentTime }: Props) {
  const [fromNow, setFromNow] = useState(false);
  const [copied, setCopied] = useState(false);
  const link = withStartTime(url, fromNow && currentTime ? currentTime : null);
  const text = `Assista: ${title}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Sem permissão de clipboard: seleciona o campo para o Ctrl+C manual.
      document.querySelector<HTMLInputElement>('#share-link')?.select();
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Compartilhar aula" description={title}>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {TARGETS.map((t) => (
          <a
            key={t.id}
            href={shareUrl(t.id, link, text)}
            target="_blank"
            rel="noreferrer noopener"
            className="group flex flex-col items-center gap-1.5 rounded-2xl p-2 text-center text-[0.7rem] text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
          >
            <span
              className="flex size-11 items-center justify-center rounded-full text-white transition-transform group-hover:scale-105"
              style={{ background: t.color }}
            >
              {t.icon}
            </span>
            {t.label}
          </a>
        ))}
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            type="button"
            onClick={() => void navigator.share({ title, text, url: link }).catch(() => {})}
            className="group flex flex-col items-center gap-1.5 rounded-2xl p-2 text-[0.7rem] text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
          >
            <span className="flex size-11 items-center justify-center rounded-full bg-surface-2 text-fg transition-transform group-hover:scale-105">
              <ShareNetwork aria-hidden className="size-5" />
            </span>
            Mais
          </button>
        )}
      </div>

      <div className="focus-ring-within mt-5 flex items-center gap-2 rounded-2xl bg-surface-2 p-1.5 pl-3">
        <LinkSimple aria-hidden className="size-4 shrink-0 text-fg-subtle" />
        <input
          id="share-link"
          readOnly
          value={link}
          aria-label="Link da aula"
          onFocus={(e) => e.target.select()}
          className="min-w-0 flex-1 bg-transparent text-sm text-fg-muted focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void copy()}
          className={cn(
            'flex h-9 shrink-0 items-center gap-1.5 rounded-xl px-3 text-sm font-medium transition-colors',
            copied
              ? 'bg-emerald-500/15 text-emerald-300 light:text-emerald-700'
              : 'bg-brand-400 text-on-brand hover:brightness-110',
          )}
        >
          {copied ? <Check aria-hidden className="size-4" /> : <Copy aria-hidden className="size-4" />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      {!!currentTime && currentTime > 1 && (
        <label className="mt-4 flex items-center gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            checked={fromNow}
            onChange={(e) => setFromNow(e.target.checked)}
            className="size-4 accent-brand-600"
          />
          Começar em {formatTime(currentTime)}
        </label>
      )}
    </Dialog>
  );
}

const svg = (d: string): ReactNode => (
  <svg aria-hidden viewBox="0 0 24 24" className="size-5" fill="currentColor">
    <path d={d} />
  </svg>
);

const TARGETS: { id: ShareTarget; label: string; color: string; icon: ReactNode }[] = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    color: '#25D366',
    icon: svg(
      'M17.5 14.4c-.3-.1-1.8-.9-2-1s-.5-.1-.7.1-.8 1-1 1.2-.4.2-.7.1a8.2 8.2 0 0 1-4-3.5c-.3-.5.3-.5.9-1.6.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6a1.1 1.1 0 0 0-.8.4 3.4 3.4 0 0 0-1 2.5 5.9 5.9 0 0 0 1.2 3.1 13.4 13.4 0 0 0 5.2 4.6c1.9.8 2.7.9 3.6.7a3.1 3.1 0 0 0 2-1.4 2.5 2.5 0 0 0 .2-1.4c-.1-.2-.3-.3-.7-.4ZM12 2a10 10 0 0 0-8.6 15l-1.4 5 5.1-1.3A10 10 0 1 0 12 2Z',
    ),
  },
  {
    id: 'telegram',
    label: 'Telegram',
    color: '#2AABEE',
    icon: svg(
      'M21.9 4.6 18.8 19c-.2 1-.8 1.3-1.7.8l-4.6-3.4-2.2 2.1c-.3.3-.5.5-1 .5l.3-4.7 8.6-7.8c.4-.3-.1-.5-.6-.2L7 13l-4.6-1.4c-1-.3-1-1 .2-1.5l18-6.9c.8-.3 1.6.2 1.3 1.4Z',
    ),
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    color: '#0A66C2',
    icon: svg(
      'M6.9 8.9H3.6V20h3.3V8.9ZM5.3 3.5a1.9 1.9 0 1 0 0 3.8 1.9 1.9 0 0 0 0-3.8ZM20.4 13.7c0-3-1.6-5-4.3-5a3.7 3.7 0 0 0-3.3 1.8V8.9H9.6V20h3.3v-5.5c0-1.4.3-2.8 2-2.8s1.7 1.6 1.7 2.9V20h3.3l.5-6.3Z',
    ),
  },
  {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    icon: svg('M14 8V6.2c0-.8.2-1.2 1.4-1.2H17V2h-2.4C11.7 2 11 3.6 11 6v2H9v3h2v11h3V11h2.6L17 8h-3Z'),
  },
  {
    id: 'x',
    label: 'X',
    color: '#000000',
    icon: svg(
      'M17.8 3h3L14.2 10.6 22 21h-6.1l-4.8-6.3L5.6 21h-3l7-8L2 3h6.2l4.4 5.8L17.8 3Zm-1 16.2h1.7L7.3 4.7H5.5l11.3 14.5Z',
    ),
  },
  { id: 'email', label: 'E-mail', color: '#6b7280', icon: <EnvelopeSimple aria-hidden className="size-5" /> },
];
